<template>
  <article
    class="tarjeta"
    @click="$emit('abrir', parada)"
  >
    <div class="miniatura">
      <div
        v-if="cargandoPrincipal"
        class="capa-carga"
      >
        <v-progress-circular
          indeterminate
          color="primary"
          size="20"
          width="2"
        />
      </div>
      <v-img
        v-else
        :src="principal"
        :alt="`Foto de ${parada.targetName}`"
        cover
        eager
        :aspect-ratio="1"
        class="imagen"
      />
    </div>

    <div class="cuerpo">
      <div class="tipo">
        <v-icon size="13">
          {{ icono }}
        </v-icon>
        {{ etiquetaTipo }}
      </div>

      <h2 class="nombre">
        {{ parada.targetName }}
      </h2>
      <!-- En una parada de producto, de quien es pesa mas que la ciudad: es
           lo que te dice a que restaurante entrar. -->
      <div
        v-if="parada.companyName || parada.city"
        class="ciudad"
      >
        <template v-if="parada.companyName">
          <v-icon size="12">mdi-storefront-outline</v-icon>
          {{ parada.companyName }}
        </template>
        <template v-if="parada.companyName && parada.city"> · </template>
        <template v-if="parada.city">{{ parada.city }}</template>
      </div>

      <p
        v-if="parada.note"
        class="nota"
      >
        {{ parada.note }}
      </p>

      <!-- SEGUNDA TANDA. Llega cuando la miniatura ya esta, y lo que trae
           depende del tipo de parada: un producto enseña tambien fotos de su
           empresa, una empresa enseña sus productos, y un lugar sus propias
           fotos. Va aqui y no en la columna izquierda para no achicar la
           principal ni empujar el texto. -->
      <div
        v-if="cargandoResto"
        class="galeria"
      >
        <div class="galeria-cargando">
          <v-progress-circular
            indeterminate
            color="primary"
            size="16"
            width="2"
          />
        </div>
      </div>

      <div
        v-for="grupo in gruposVisibles"
        v-else
        :key="grupo.id"
        class="grupo-fotos"
      >
        <!-- El rotulo solo cuando hay mas de un grupo: con uno solo sobra, y
             en una parada de producto es justo lo que hacia falta para saber
             cual foto es del plato y cual del local. -->
        <div
          v-if="grupo.titulo"
          class="grupo-rotulo"
        >
          {{ grupo.titulo }}
        </div>
        <div class="galeria">
          <div
            v-for="foto in grupo.items"
            :key="foto.id"
            class="foto-extra"
            :title="grupo.titulo || parada.targetName"
          >
            <v-img
              v-if="foto.imagen"
              :src="foto.imagen"
              :alt="grupo.titulo || parada.targetName"
              cover
              eager
              :aspect-ratio="1"
            />
            <div
              v-else
              class="galeria-cargando"
            >
              <v-progress-circular
                indeterminate
                color="primary"
                size="14"
                width="2"
              />
            </div>
          </div>
        </div>
      </div>

      <div class="pie">
        <span
          v-if="parada.price"
          class="dato dato-precio"
        >
          {{ enPesos(parada.price) }}
        </span>
        <span
          v-if="parada.estimatedMinutes"
          class="dato"
        >
          <v-icon size="13">mdi-clock-outline</v-icon>
          {{ parada.estimatedMinutes }} min
        </span>
        <span class="ir">
          Ver
          <v-icon size="15">mdi-arrow-right</v-icon>
        </span>
      </div>
    </div>
  </article>
</template>

<script>
import apisImagenes from '@/apis/apisImagenes'
import apisProducts from '@/apis/apisProducts'
import imagenPorDefecto from '@/assets/imagen-none.jpg'
import { enPesos } from '@/utils/configuracion'

/**
 * Una parada dentro del recorrido de una ruta.
 *
 * Recibe la parada ya resuelta por el backend (tipo, nombre, ciudad) y pide sus
 * FOTOS por su cuenta, en DOS TANDAS:
 *
 *   1. la principal, con ?limite=1. Es la miniatura, y es lo unico que espera
 *      la tarjeta para dejar de estar vacia.
 *   2. las demas, solo cuando la principal ya esta pintada.
 *
 * Que sean dos peticiones y no una es lo que hace que la tanda tenga sentido:
 * el endpoint viejo devolvia TODAS las imagenes de la entidad de golpe, asi que
 * enseñar una miniatura de un sitio con cinco fotos obligaba a descargar las
 * cinco en base64 antes de poder pintar nada.
 *
 * LA SEGUNDA TANDA CAMBIA SEGUN EL TIPO, porque lo que le falta a cada parada
 * es distinto:
 *
 *   - lugar:    sus propias fotos. No hay nada mas que enseñar.
 *   - producto: el resto de las suyas MAS fotos de la empresa que lo hace. El
 *               plato solo no dice donde estas parado.
 *   - empresa:  sus productos, una foto por producto. De un local interesa que
 *               vende, no mas fotos de la fachada.
 *
 * Ademas cada parada carga por su lado: una con fotos pesadas no retrasa a las
 * de arriba ni a las de abajo, y el texto del recorrido se lee entero desde el
 * primer momento.
 */
export default {
  name: 'TarjetaParada',
  /** Cuantas fotos extra se muestran como mucho. */
  MAX_EXTRA: 4,
  props: {
    parada: {
      type: Object,
      required: true
    }
  },
  emits: ['abrir'],
  data() {
    return {
      imagenPrincipal: null,
      cargandoPrincipal: true,
      // [{ id, titulo, items: [{ id, imagen, estado }] }]. Los huecos existen
      // desde el principio, asi la tira no da saltos segun van llegando.
      grupos: [],
      cargandoResto: false
    }
  },
  computed: {
    /** "empresa", "lugar" o "producto". Es el mismo que usa la tabla imagenes. */
    tipo() {
      return this.parada.targetType
    },
    icono() {
      if (this.tipo === 'empresa') {
        return 'mdi-storefront-outline'
      }
      if (this.tipo === 'producto') {
        return 'mdi-food'
      }
      return 'mdi-map-marker-outline'
    },
    etiquetaTipo() {
      if (this.tipo === 'empresa') {
        return 'Empresa'
      }
      if (this.tipo === 'producto') {
        return 'Producto'
      }
      return 'Lugar'
    },
    principal() {
      return this.imagenPrincipal || imagenPorDefecto
    },
    /**
     * Los grupos que siguen en pie, sin las fotos que no llegaron.
     *
     * Se filtra aqui y NO se saca del array de origen: las peticiones estan en
     * vuelo y cada una escribe en su posicion, asi que mover el array a mitad
     * de camino hace que una respuesta caiga en la miniatura equivocada.
     */
    gruposVisibles() {
      return this.grupos
        .map(g => ({ ...g, items: g.items.filter(i => i.estado !== 'sin') }))
        .filter(g => g.items.length > 0)
    }
  },
  created() {
    this.cargarPrincipal()
  },
  methods: {
    enPesos,
    /** Primera tanda: una sola foto. */
    cargarPrincipal() {
      if (!this.parada.idTarget) {
        this.cargandoPrincipal = false
        return
      }

      this.cargandoPrincipal = true

      apisImagenes.principal(this.tipo, this.parada.idTarget)
        .then(imagen => {
          this.imagenPrincipal = imagen
        })
        // Que no haya foto no es motivo para romper la tarjeta
        .catch(() => {
          this.imagenPrincipal = null
        })
        .finally(() => {
          this.cargandoPrincipal = false

          // Encadenada, no en paralelo: el resto arranca cuando la miniatura ya
          // esta. Si no hubo ni principal, esta entidad no tiene fotos.
          if (this.imagenPrincipal) {
            this.cargarResto()
          }
        })
    },
    /** Empaqueta unas fotos ya resueltas como un grupo, o nada si no hay. */
    comoGrupo(titulo, prefijo, fotos) {
      const lista = fotos || []

      if (lista.length === 0) {
        return []
      }
      return [{
        id: prefijo,
        titulo,
        items: lista.map((f, i) => ({ id: `${prefijo}-${i}`, imagen: f, estado: 'ok' }))
      }]
    },
    /**
     * Segunda tanda. Lo que trae depende del tipo de parada:
     *
     *   - lugar:    sus propias fotos. No hay nada mas que enseñar.
     *   - producto: el resto de las suyas MAS fotos de la empresa que lo hace.
     *               El plato solo no dice donde estas parado.
     *   - empresa:  sus productos. De un local interesa que vende, no mas
     *               fotos de la fachada.
     */
    cargarResto() {
      if (this.tipo === 'producto') {
        this.cargarProductoYEmpresa()
      } else if (this.tipo === 'empresa') {
        this.cargarProductosDeEmpresa()
      } else {
        this.cargarPropias()
      }
    },
    /** Lugar: el resto de sus imagenes. Una sola peticion. */
    cargarPropias() {
      this.cargandoResto = true

      apisImagenes.resto(this.tipo, this.parada.idTarget, this.$options.MAX_EXTRA)
        .then(fotos => {
          // Un solo grupo, asi que sin rotulo: no hay de que distinguirlo
          this.grupos = this.comoGrupo(null, 'propia', fotos)
        })
        .catch(() => {
          this.grupos = []
        })
        .finally(() => {
          this.cargandoResto = false
        })
    },
    /** Producto: las suyas primero, y detras las de su empresa. */
    async cargarProductoYEmpresa() {
      this.cargandoResto = true

      const mitad = Math.floor(this.$options.MAX_EXTRA / 2)
      const suyas = await apisImagenes.resto('producto', this.parada.idTarget, mitad).catch(() => [])

      let deLaEmpresa = []

      if (this.parada.idCompany) {
        deLaEmpresa = await apisImagenes
          .deEntidad('empresa', this.parada.idCompany, 0, this.$options.MAX_EXTRA - suyas.length)
          .then(r => r.map(i => i.pathBase64))
          .catch(() => [])
      }

      // Dos grupos rotulados y no una tira corrida: mezcladas no hay forma de
      // saber cual foto es del plato y cual del local.
      this.grupos = [
        ...this.comoGrupo('Del producto', 'p', suyas),
        ...this.comoGrupo(this.parada.companyName || 'Del local', 'e', deLaEmpresa)
      ]
      this.cargandoResto = false
    },
    /**
     * Empresa: una foto por producto, la principal de cada uno.
     *
     * La lista de productos es una peticion, y despues una por producto. Se
     * podrian pedir todas las imagenes de golpe, pero eso traeria TODAS las
     * fotos de TODOS los productos para enseñar cuatro miniaturas.
     */
    cargarProductosDeEmpresa() {
      this.cargandoResto = true

      apisProducts.getProductsCompany(this.parada.idTarget)
        .then(productos => {
          const primeros = (productos || []).slice(0, this.$options.MAX_EXTRA)

          this.grupos = primeros.length === 0 ? [] : [{
            id: 'productos',
            titulo: 'Lo que vende',
            items: primeros.map(p => ({
              id: `prod-${p.idProduct}`, imagen: null, estado: 'cargando'
            }))
          }]
          this.cargandoResto = false

          primeros.forEach((p, indice) => {
            apisImagenes.principal('producto', p.idProduct)
              .catch(() => null)
              .then(imagen => {
                const item = this.grupos[0] && this.grupos[0].items[indice]

                if (!item) {
                  return
                }
                if (imagen) {
                  item.imagen = imagen
                  item.estado = 'ok'
                } else {
                  // Un producto sin foto no deja un hueco girando para siempre
                  item.estado = 'sin'
                }
              })
          })
        })
        .catch(() => {
          this.grupos = []
          this.cargandoResto = false
        })
    }
  }
}
</script>

<style scoped>
.tarjeta {
  display: flex;
  gap: 12px;
  flex: 1 1 auto;
  min-width: 0;
  margin-bottom: 14px;
  padding: 12px;
  border: 1px solid #e4e7ec;
  border-radius: 12px;
  background: #fff;
  cursor: pointer;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}
.tarjeta:hover {
  border-color: #1976d2;
  box-shadow: 0 4px 14px rgba(16, 42, 76, 0.1);
}

.miniatura {
  position: relative;
  flex: 0 0 auto;
  /* align-self, no solo aspect-ratio: la tarjeta es un flex en fila y por
     defecto estira sus hijos a lo alto. Con el alto impuesto la proporcion se
     ignora, y la miniatura salia de 96 x 191 en vez de cuadrada. */
  align-self: flex-start;
  width: 136px;
  aspect-ratio: 1;
  min-height: 0;
  border-radius: 8px;
  overflow: hidden;
  background: #f2f5f9;
}
.imagen {
  width: 100%;
  height: 100%;
}
.capa-carga {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f2f5f9;
}

.cuerpo {
  flex: 1 1 auto;
  min-width: 0;
}
.tipo {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 0.66rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #1976d2;
}
.nombre {
  margin: 2px 0 0;
  font-size: 1.02rem;
  font-weight: 700;
  line-height: 1.25;
  color: #1a1a1a;
  overflow-wrap: anywhere;
}
.ciudad {
  font-size: 0.76rem;
  color: #98a2b3;
}
.nota {
  margin: 7px 0 0;
  font-size: 0.86rem;
  line-height: 1.45;
  color: #667085;
}

/* La galeria no envuelve: si no caben, se desplazan de lado. Envolviendo, una
   parada con cuatro fotos crecia el doble de alto que las demas y el recorrido
   dejaba de leerse como una secuencia pareja. */
.grupo-fotos {
  margin-top: 10px;
}
.grupo-rotulo {
  font-size: 0.62rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #98a2b3;
  margin-bottom: 4px;
}
.galeria {
  display: flex;
  align-items: center;
  gap: 6px;
  overflow-x: auto;
  padding-bottom: 2px;
}
.galeria-cargando {
  flex: 0 0 auto;
  width: 64px;
  height: 64px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 7px;
  background: #f2f5f9;
}
.foto-extra {
  flex: 0 0 auto;
  width: 64px;
  height: 64px;
  border-radius: 7px;
  overflow: hidden;
  background: #f2f5f9;
}

.pie {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-top: 10px;
}
.dato {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  border-radius: 9px;
  background: #f2f5f9;
  color: #475467;
  font-size: 0.74rem;
  font-weight: 600;
}
/* El precio es el dato que se mira primero en una ruta de platos */
.dato-precio {
  background: #e6f5ee;
  color: #0f7a52;
}
.ir {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  margin-left: auto;
  color: #1976d2;
  font-size: 0.8rem;
  font-weight: 700;
}

/* En celular la miniatura se achica: a 136px, la foto mas el texto deja el
   nombre en dos o tres lineas y la tarjeta se ve apretada */
@media (max-width: 600px) {
  .miniatura {
    width: 104px;
  }
}
@media (max-width: 420px) {
  .miniatura {
    width: 88px;
  }
  .galeria-cargando,
  .foto-extra {
    width: 48px;
    height: 48px;
  }
}
</style>
